/**
 * /workspaces — Work-mode home: the all-workspaces directory.
 *
 * Mockup parity (mockup `WorkDirectory.jsx`): compact cards on the bare desk —
 * title + member-avatar stack on one row, then "N canvases · M artifacts", then
 * a short "Updated …" line. A search box filters by name. Counts + member names
 * come straight off the list endpoint (all CD-local), so there's no per-card
 * fan-out. Switching from Plan → Work lands here (see ViewSwitcher).
 *
 * Built on CD's DeskScreen (surface="ws-dir-wrap", no white sheet) + the ported
 * plan-skin `ws-*` classes — the same stack as the rest of the ported screens.
 */

import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { LayoutGrid, Search, TriangleAlert } from "lucide-react";

import { DeskScreen } from "@/components/plan/DeskScreen";
import { initials } from "@/components/settings/settingsKit";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "../../api";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/workspaces")({
  component: WorkspacesPage,
});

// Short, scannable relative time (mockup shortTime): "about 8 hours" → "8h ago",
// "less than a minute" → "Few min ago", "1 day" → "1d ago".
function shortTime(iso: string): string {
  if (!iso) return "";
  const s = formatDistanceToNow(new Date(iso)).toLowerCase();
  if (s.includes("less than") || s.includes("second") || s.includes("minute"))
    return "Few min ago";
  const m = s.match(/(\d+)\s*(hour|day|week|month|year)/);
  if (m) {
    const n = m[1] ?? "";
    const unit = m[2] ?? "";
    return `${n}${unit === "month" ? "mo" : unit.charAt(0)} ago`;
  }
  // Singular forms ("about an hour", "a day", "about a month").
  if (s.includes("hour")) return "1h ago";
  if (s.includes("day")) return "1d ago";
  if (s.includes("week")) return "1w ago";
  if (s.includes("month")) return "1mo ago";
  if (s.includes("year")) return "1y ago";
  return `${s} ago`;
}

function WorkspacesPage() {
  useDocumentTitle(["Workspaces"]);
  const api = useApi();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.listWorkspaces(),
  });

  const all = workspaces.data ?? [];
  const needle = q.trim().toLowerCase();
  const shown = needle ? all.filter((w) => w.name.toLowerCase().includes(needle)) : all;

  const toolbar = (
    <h1 className="ws-tb-title">
      Workspaces{" "}
      <span style={{ fontWeight: 400, color: "var(--ink-faint)" }}>({all.length})</span>
    </h1>
  );

  return (
    <DeskScreen toolbar={toolbar} surface="ws-dir-wrap">
      <div className="searchbar" style={{ margin: "0 0 18px" }}>
        <Search size={16} className="search-ico" />
        <input
          className="search-input"
          placeholder="Search workspaces…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {workspaces.isLoading ? (
        <WorkspaceGridSkeleton />
      ) : workspaces.isError ? (
        // A failed request is NOT the same as "no access" — show an error +
        // retry, never the "ask an admin" empty state (that misleads the user
        // into thinking their access was revoked when the service is just down).
        <Empty className="bg-transparent">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <TriangleAlert />
            </EmptyMedia>
            <EmptyTitle>Couldn't load workspaces</EmptyTitle>
            <EmptyDescription>
              The workspace service is temporarily unavailable. Your access hasn't
              changed — please try again in a moment.
            </EmptyDescription>
          </EmptyHeader>
          <Button variant="outline" onClick={() => void workspaces.refetch()}>
            Retry
          </Button>
        </Empty>
      ) : all.length === 0 ? (
        <Empty className="bg-transparent">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <LayoutGrid />
            </EmptyMedia>
            <EmptyTitle>No workspaces yet</EmptyTitle>
            <EmptyDescription>
              You haven't been granted access to any workspace yet. Ask an org admin to add
              you to a workspace.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : shown.length === 0 ? (
        <p className="sec-note" style={{ color: "var(--ink-soft)" }}>
          No workspaces match “{q.trim()}”.
        </p>
      ) : (
        <div className="ws-dir-grid" data-tour="workspace-grid">
          {shown.map((w) => (
            <button
              key={w.id}
              type="button"
              className="ws-card"
              onClick={() => void navigate({ to: "/w/$slug", params: { slug: w.slug } })}
            >
              <div className="ws-card-top">
                <span className="ws-card-title">
                  <span className="ws-dir-ico">
                    <LayoutGrid size={14} />
                  </span>{" "}
                  {w.name}
                </span>
                <AvatarGroup names={w.member_names} count={w.members_count ?? 0} />
              </div>
              <div className="ws-card-counts">
                {w.canvases_count ?? 0} {w.canvases_count === 1 ? "canvas" : "canvases"} ·{" "}
                {w.artifacts_count ?? 0} {w.artifacts_count === 1 ? "artifact" : "artifacts"}
              </div>
              <div className="ws-card-updated">Updated {shortTime(w.updated_at)}</div>
            </button>
          ))}
        </div>
      )}
    </DeskScreen>
  );
}

/** Overlapping member-avatar stack (mockup AvatarGroup): up to `max` initials,
 * then a "+N" overflow bubble. `count` is the true member total. */
function AvatarGroup({ names, count, max = 3 }: { names: string[]; count: number; max?: number }) {
  const total = count || names.length;
  if (!total) return null;
  const head = names.slice(0, max);
  const extra = total - head.length;
  const title = names.length
    ? `${total} member${total === 1 ? "" : "s"}: ${names.join(", ")}`
    : `${total} member${total === 1 ? "" : "s"}`;
  return (
    <span className="avstack" title={title}>
      {head.map((nm, i) => (
        <span key={i} className="mini-avatar sm">
          {initials(nm)}
        </span>
      ))}
      {extra > 0 && <span className="mini-avatar sm more">+{extra}</span>}
    </span>
  );
}

function WorkspaceGridSkeleton() {
  return (
    <div className="ws-dir-grid">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-[104px] w-full rounded-xl" />
      ))}
    </div>
  );
}
